import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useLoaderData, useRevalidator } from "react-router-dom";
import { toast } from "react-toastify";
import { Result } from "../../types/Result";
import { sendSmtpTestMail, updateSmtpAccount } from "./api";
import { SmtpAccountStatus, SmtpAccountsOverview } from "./type";

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString("tr-TR") : "-");

function SummaryCard({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 160 }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" sx={{ color }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function StatusChip({ a }: { a: SmtpAccountStatus }) {
  if (!a.isEnabled) return <Chip size="small" label="Pasif" color="default" />;
  if (a.quotaExhausted) return <Chip size="small" label="Kota doldu" color="error" />;
  if (a.remaining <= Math.max(10, a.dailyLimit * 0.1))
    return <Chip size="small" label="Kota bitmek üzere" color="warning" />;
  return <Chip size="small" label="Kullanılabilir" color="success" />;
}

function AccountRow({ account, onChanged, windowLabel }: { account: SmtpAccountStatus; onChanged: () => void; windowLabel: string }) {
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState<string>(account.hasCustomLimit ? String(account.dailyLimit) : "");
  const [testOpen, setTestOpen] = useState(false);
  const [testTo, setTestTo] = useState("");

  const save = async (isEnabled: boolean, dailyLimit: number | null) => {
    setBusy(true);
    try {
      const res = await updateSmtpAccount(account.name, { isEnabled, dailyLimit });
      if (res.data.isSuccess) {
        toast.success(`${account.name} güncellendi`);
        onChanged();
      } else {
        toast.error(res.data.errors?.[0] || "Güncellenemedi");
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.errors?.[0] || "Güncellenemedi");
    } finally {
      setBusy(false);
    }
  };

  const saveLimit = () => {
    const trimmed = limit.trim();
    if (trimmed === "") return save(account.isEnabled, null);
    const n = Number(trimmed);
    if (!Number.isInteger(n) || n <= 0) {
      toast.error("Günlük limit pozitif bir tam sayı olmalı");
      return;
    }
    return save(account.isEnabled, n);
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const res = await sendSmtpTestMail(account.name, testTo);
      if (res.data.isSuccess) {
        toast.success(`Test maili ${account.name} üzerinden gönderildi. Gelen kutusunu ve spam klasörünü kontrol edin.`);
        setTestOpen(false);
        onChanged();
      } else {
        toast.error(res.data.errors?.join(" | ") || "Gönderilemedi");
      }
    } catch (e: any) {
      const errors: string[] | undefined = e?.response?.data?.errors;
      toast.error(errors?.join(" | ") || "Gönderilemedi");
    } finally {
      setBusy(false);
    }
  };

  const pct = account.dailyLimit > 0 ? Math.min(100, (account.sentToday / account.dailyLimit) * 100) : 0;

  return (
    <TableRow hover sx={{ opacity: account.isEnabled ? 1 : 0.6 }}>
      <TableCell>
        <Typography variant="subtitle2">{account.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {account.smtpUser}
        </Typography>
        <br />
        <Typography variant="caption" color="text.secondary">
          {account.smtpServer}:{account.smtpPort} · {account.useOAuth ? "OAuth" : account.useSsl ? "SSL" : "STARTTLS"} · öncelik{" "}
          {account.priority}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Tooltip title={account.isEnabled ? "Pasife al: bu hesap gönderimde kullanılmaz" : "Etkinleştir"}>
          <Switch checked={account.isEnabled} disabled={busy} onChange={(e) => save(e.target.checked, account.hasCustomLimit ? account.dailyLimit : null)} />
        </Tooltip>
      </TableCell>
      <TableCell sx={{ minWidth: 220 }}>
        <Stack spacing={0.5}>
          <Typography variant="body2">
            {account.sentToday} / {account.dailyLimit} gönderildi · kalan <strong>{account.remaining}</strong>
          </Typography>
          <LinearProgress
            variant="determinate"
            value={pct}
            color={account.quotaExhausted ? "error" : pct >= 90 ? "warning" : "primary"}
          />
        </Stack>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            type="number"
            placeholder="varsayılan"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            disabled={busy}
            sx={{ width: 120 }}
            slotProps={{ htmlInput: { min: 1 } }}
          />
          <Button size="small" variant="outlined" disabled={busy} onClick={saveLimit}>
            Kaydet
          </Button>
        </Stack>
      </TableCell>
      <TableCell>
        <StatusChip a={account} />
      </TableCell>
      <TableCell>
        <Typography variant="caption">{fmt(account.lastSentTime)}</Typography>
      </TableCell>
      <TableCell align="right">
        <Button size="small" startIcon={<SendIcon />} disabled={busy} onClick={() => setTestOpen(true)}>
          Test
        </Button>
        <Dialog open={testOpen} onClose={() => !busy && setTestOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>Test maili: {account.name}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Mail bu hesap üzerinden, etkin/kota durumuna bakılmaksızın gönderilir ve kullanıma sayılır ({windowLabel.toLowerCase()}).
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              type="email"
              label="Alıcı e-posta"
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTestOpen(false)} disabled={busy}>
              Vazgeç
            </Button>
            <Button variant="contained" onClick={sendTest} disabled={busy || !testTo.includes("@")}>
              Gönder
            </Button>
          </DialogActions>
        </Dialog>
      </TableCell>
    </TableRow>
  );
}

export default function SmtpAccountsPanel() {
  const data = useLoaderData() as Result<SmtpAccountsOverview>;
  const revalidator = useRevalidator();
  const overview = data.data;

  if (!overview) {
    return <Alert severity="error">{data.errors?.[0] || "SMTP hesapları yüklenemedi"}</Alert>;
  }

  const refresh = () => revalidator.revalidate();
  const windowStart = new Date(overview.quotaDayStartUtc).toLocaleString("tr-TR");
  const rolling = overview.quotaWindowHours > 0;
  const windowLabel = rolling ? `Son ${overview.quotaWindowHours} saat` : "Bugün";

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Mail Sunucuları</Typography>
          <Typography variant="caption" color="text.secondary">
            {rolling
              ? `Kota penceresi: son ${overview.quotaWindowHours} saat (${windowStart} itibarıyla).`
              : `Kota günü başlangıcı: ${windowStart} (${overview.quotaTimeZone}).`}{" "}
            Gönderimler etkin ve kotası dolmamış hesaplara sırayla dağıtılır.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={refresh} disabled={revalidator.state === "loading"}>
          Yenile
        </Button>
      </Stack>

      {overview.availableAccounts === 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Şu an gönderim yapabilecek hesap yok. Tüm hesaplar pasif veya günlük kotası dolu; yeni mailler kuyrukta bekler ve
          kota sıfırlanınca otomatik gönderilir.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <SummaryCard label={`${windowLabel} gönderilen`} value={overview.totalSentToday} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <SummaryCard label="Kalan (etkin hesaplar)" value={overview.totalRemaining} color={overview.totalRemaining === 0 ? "error.main" : undefined} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <SummaryCard label="Toplam limit (etkin hesaplar)" value={overview.totalDailyLimit} />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <SummaryCard
            label="Kullanılabilir hesap"
            value={`${overview.availableAccounts} / ${overview.accounts.length}`}
            color={overview.availableAccounts === 0 ? "error.main" : undefined}
          />
        </Grid>
      </Grid>

      <Card variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Hesap</TableCell>
              <TableCell align="center">Etkin</TableCell>
              <TableCell>{windowLabel.toLowerCase() === "bugün" ? "Bugünkü kullanım" : `${windowLabel} kullanım`}</TableCell>
              <TableCell>Günlük limit</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell>Son gönderim</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {overview.accounts.map((a) => (
              <AccountRow key={a.name} account={a} onChanged={refresh} windowLabel={windowLabel} />
            ))}
          </TableBody>
        </Table>
      </Card>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
        Kullanım sayısı, Email Logları'nda bu hesap üzerinden "Gönderildi" olan kayıtlardan hesaplanır ({windowLabel.toLowerCase()}).
        Sağlayıcı (Natro) limiti takvim gününe göre değil kayan pencereyle ve maili kabul ettikten sonra uyguladığı için kota dolan
        hesaptan çıkan mailler sessizce geri döner; bu yüzden limit dolmadan hesap otomatik devre dışı bırakılır.
      </Typography>
    </Box>
  );
}
